<template>
	<App :title="`Credit Plan - ${credit?.rider?.firstname || ''} ${credit?.rider?.lastname || ''}`">

        <!-- Header Section -->
        <div class="mb-4 pb-3 border-bottom">
            <h3 class="fw-bold mb-1">Credit Plan Details</h3>
            <div class="d-flex align-items-center gap-2 text-muted" v-if="credit?.rider">
                <i class="fas fa-user-circle fs-5"></i>
                <span class="fs-6">Rider: <span class="fw-semibold text-dark">{{ credit.rider.firstname }} {{ credit.rider.lastname }}</span></span>
            </div>
        </div>

         <!-- Credit Agreement Document -->
    <div class="my-4" v-if="credit?.credit_agreement">
        <div class="d-flex align-items-center gap-2 mb-3">
            <i class="fas fa-file-contract text-muted"></i>
            <div>
                <h6 class="fw-bold mb-0">Legal Documents</h6>
                <div class="small text-muted">Signed documents attached to this credit plan</div>
            </div>
        </div>
        <div class="border rounded-3 p-3 bg-white d-flex align-items-center justify-content-between gap-3">
            <div class="d-flex align-items-center gap-3">
                <div class="rounded-2 bg-light border d-flex align-items-center justify-content-center" style="width:48px; height:48px; flex-shrink:0;">
                    <i class="fas fa-file-alt text-secondary fs-5"></i>
                </div>
                <div>
                    <div class="fw-semibold" style="font-size:0.875rem;">Credit Agreement</div>
                    <div class="text-muted" style="font-size:0.78rem;">{{ credit.credit_agreement.name }}</div>
                </div>
            </div>
            <button type="button" @click="openDocPreview(credit.credit_agreement.url, credit.credit_agreement.name || 'Credit Agreement')" class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1">
                <i class="fas fa-eye me-1"></i> View
            </button>
        </div>
    </div>
        <CreditPlanTable
        :credit-data="credit"
        title="Credit Repayment Schedule"
        subtitle="Track and manage credit repayments"
        credit-type ="credit"
        update-endpoint="credit.ajax.repayment"
        @toggle-success="refreshData"
        />

   
    
		
	</App>

	<!-- Document Preview Modal -->
	<Teleport to="body">
		<div v-if="docPreview.open" class="rider-doc-lightbox" @click.self="closeDocPreview">
			<div class="rider-doc-lightbox-inner">
				<div class="rider-doc-lightbox-header">
					<span class="rider-doc-lightbox-title">{{ docPreview.label }}</span>
					<button type="button" class="rider-doc-lightbox-close" @click="closeDocPreview">
						<i class="fas fa-times"></i>
					</button>
				</div>
				<div class="rider-doc-lightbox-body">
					<img :src="docPreview.url" :alt="docPreview.label" class="rider-doc-lightbox-img" />
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
// ─── Lightbox ─────────────────────────────────────────────────────────────────
.rider-doc-lightbox {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(0, 0, 0, 0.78);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.rider-doc-lightbox-inner {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    max-width: 860px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
}

.rider-doc-lightbox-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid #e9ecef;
    background: #f9fafb;
}

.rider-doc-lightbox-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #111827;
}

.rider-doc-lightbox-close {
    background: none;
    border: none;
    font-size: 1rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;

    &:hover { background: #f3f4f6; color: #111827; }
}

.rider-doc-lightbox-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    background: #111;
    max-height: 80vh;
    overflow: auto;
}

.rider-doc-lightbox-img {
    max-width: 100%;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 4px;
}
</style>

<script lang="ts">

import $ from 'jquery'
import banks from '@/json/banks.json' // Your JSON file
import { useConfirm } from '@/libs/useConfirm.ts'
import locations from '@/json/location.json' 
import { error } from '@/ts/app'
import Swal from 'sweetalert2'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'
import { Notivue, Notification, push, materialTheme, NotificationProgress, NotivueKeyboard  } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'
import CreditPlanTable from '@/vue/components/CreditPlanTable.vue'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		PhoneField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		Notification,
		NotificationProgress,
		AppNotification,
		NotivueKeyboard,
        CreditPlanTable
	
	},
})
class Single extends Vue {
	message = ''
	confirm = useConfirm().confirm
	loading = false
	done = false
    creditUpdateKey = 0
    hasActiveCreditPlan = true

    // Document preview lightbox
    docPreview = { open: false, url: '', label: '' }

    @Prop({type:Object})
    credit!: any

    refreshData() {
        this.router.reload()
    }

    openDocPreview(url: string, label: string) {
        this.docPreview = { open: true, url, label }
    }

    closeDocPreview() {
        this.docPreview = { open: false, url: '', label: '' }
    }
}

export default toNative(Single)
</script>

